import React, { ChangeEvent, FormEvent, useRef, useState } from 'react'
import { Box, Button, Checkbox, Flex, Heading, Text } from '@workwhile/ui'
import { useUploadVmsTimesheetMutation } from 'queries/admin/vms/useUploadVmsTimesheetMutation'

const COMPANY_ID = 3

const VmsTimesheetUpload = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [isDryRun, setIsDryRun] = useState(true)

  const {
    mutate: uploadTimesheet,
    isPending,
    data,
    error,
    reset,
  } = useUploadVmsTimesheetMutation()

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!file) return
    // Clear stale data/error so a prior success doesn't render alongside a new
    // error (React Query keeps last data until reset or next success).
    reset()
    uploadTimesheet(
      { companyId: COMPANY_ID, file, dryRun: isDryRun },
      {
        onSuccess: ({ blob, filename }) => {
          downloadBlob(blob, filename)
          setFile(null)
          setIsDryRun(true)
          if (fileInputRef.current) fileInputRef.current.value = ''
        },
      }
    )
  }

  return (
    <Box m={30}>
      <Heading>VMS Timesheet Report Upload</Heading>
      <Text mt={2}>
        Upload a VMS timesheet CSV to reconcile against WorkerShiftAssoc
        records. Results download as a CSV.
      </Text>
      <Box as="ul" mt={2} mb={3} pl={4}>
        <li>
          Updates WorkerShiftAssoc started_at/completed_at to match VMS
          reporting
        </li>
        <li>Approves completed shifts after receiving final VMS reporting</li>
        <li>
          <a
            href="https://app.notion.com/p/workwhile/37559b23537a8026b5fef7c6f53576c6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Full Documentation
          </a>
        </li>
      </Box>
      <form onSubmit={handleSubmit}>
        <Box mb={3}>
          <Checkbox
            value={isDryRun}
            onChange={() => setIsDryRun((prev) => !prev)}
          >
            <Text>Dry Run (preview without applying)</Text>
          </Checkbox>
        </Box>
        <Flex gap={2} alignItems="center">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFile(e.target.files?.[0] ?? null)
            }
          />
          <Button type="submit" loading={isPending} disabled={!file}>
            {isDryRun ? 'Preview (Dry Run)' : 'Apply Changes'}
          </Button>
        </Flex>
      </form>
      {data && !isPending ? (
        <Text mt={3} color="green">
          Success — downloaded {data.filename}
        </Text>
      ) : null}
      {error && !isPending ? (
        <Box
          as="pre"
          mt={3}
          p={2}
          color="red"
          style={{
            maxHeight: 240,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            fontSize: 12,
            fontFamily: 'monospace',
          }}
        >
          {error.message}
        </Box>
      ) : null}
    </Box>
  )
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export default VmsTimesheetUpload
